import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { KeyStatType, reportChartTypeOptions, responseFilterActiveColor, responseFilterBaselineColor, responseFilterBaselineTextColor, statButtons } from '../../constants'

import { black, grey, yellow } from '../../../../../colors'
import ReportsChip from '../../ReportsChip'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CustomTooltip from '../../../../../components/CustomTooltip'
import { type KeyStatSurveyItem, type ICalculation, type ReportsV2 } from '../../../../../models/Report'
import { SurveyQuestionType } from '../../../../../models/Survey'
import StatButtonGroup from '../StatButtonGroup'
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft'
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom'
import QuestionSampleSizeRow from '../../QuestionSampleSizeRow'
import { getQuestionSampleStats } from '../../questionSampleSize'
import { getReportTitleDisplayItems } from '../../reportTitles'
import { filterVisibleCalculations, getAvailableSeriesIndexes, hasDisplayValue } from '../../helper'
import Dropdown from '../../../../../components/Dropdown'
import { ButtonSize, ButtonType } from '../../../../../components/GenericButton/styles'
import GenericButton from '../../../../../components/GenericButton'
import { type ReportChartType, type VisibleKeyStats } from '../../../types'

interface TitleProps {
  titleText: string
  reports: ReportsV2
  surveyItem: KeyStatSurveyItem | null
  calculations?: ICalculation[] | null
  comparisonCalculations?: ICalculation[] | null
  selectedTrend: number
  selectedStat?: KeyStatType
  setSelectedStat?: (stat: KeyStatType) => void
  showOrientationToggle?: boolean
  chartOrientation?: 'vertical' | 'horizontal'
  onChartOrientationChange?: (orientation: 'vertical' | 'horizontal') => void
  selectedChartType?: ReportChartType
  onChartTypeChange?: (chartType: ReportChartType) => void
  showChartTypeDropdown?: boolean
  visibleKeyStats: VisibleKeyStats
  responseFilterWarningMessage?: string | null
}

const statTypes = [
  KeyStatType.AVERAGE,
  KeyStatType.TOP_2,
  KeyStatType.BOTTOM_2
]

const Title: React.FC<TitleProps> = ({
  titleText,
  reports,
  surveyItem,
  calculations = null,
  comparisonCalculations = null,
  selectedTrend,
  selectedStat,
  setSelectedStat,
  showOrientationToggle = false,
  chartOrientation = 'vertical',
  onChartOrientationChange,
  selectedChartType = 'distribution',
  onChartTypeChange,
  showChartTypeDropdown = false,
  visibleKeyStats,
  responseFilterWarningMessage = null
}) => {
  const theme = useTheme()
  const showStatsButtons = selectedTrend > 2
  const questionSampleStats = getQuestionSampleStats({ report: surveyItem, titles: reports.titles })
  const showStats = filterVisibleCalculations(calculations ?? surveyItem?.calculations, visibleKeyStats)
    ?.filter((keyStatistic) => statTypes.includes(keyStatistic.name as KeyStatType))
  const showComparisonStats = filterVisibleCalculations(comparisonCalculations, visibleKeyStats)
    ?.filter((keyStatistic) => statTypes.includes(keyStatistic.name as KeyStatType))
  const hasShowStats = showStats?.[0] != null
  const comparisonStatsByName = new Map((showComparisonStats ?? []).map((stat) => [stat.name, stat]))
  const isLinearScaleQuestion = surveyItem?.itemType === SurveyQuestionType.LinearScale
  const hasRightSideControls = showOrientationToggle || showChartTypeDropdown || (showStatsButtons && isLinearScaleQuestion)
  const verticalOrientationTooltip = 'Display vertical bar chart'
  const horizontalOrientationTooltip = 'Display horizontal bar chart'
  const chartOrientationButtonSx = (isSelected: boolean, isLeading: boolean): Record<string, any> => ({
    minWidth: 40,
    width: 40,
    px: 0,
    ml: isLeading ? 0 : '-1px',
    borderRadius: isLeading
      ? `${theme.spacing(2)} 0 0 ${theme.spacing(2)}`
      : `0 ${theme.spacing(2)} ${theme.spacing(2)} 0`,
    '&:hover': {
      backgroundColor: grey[100]
    },
    '&[aria-pressed="true"]': {
      backgroundColor: grey[100],
      color: grey[950],
      '&:hover': {
        backgroundColor: grey[100]
      }
    },
    ...(isSelected ? { color: grey[950] } : {})
  })
  const formatStatValue = (name: string, value: number | string): string | number => {
    if (!hasDisplayValue(value)) {
      return 'N/A'
    }

    return name === KeyStatType.AVERAGE
      ? value
      : `${value}%`
  }

  let unavailableTitles: Array<{ title: string, index: number }> = []
  if (reports.titles && reports.titles.length > 1) {
    const availableSeriesIndexes = new Set(getAvailableSeriesIndexes(surveyItem, reports.titles.length))
    unavailableTitles = reports.titles
      .map((title: string, index: number) => ({ title, index }))
      .filter(({ index }) => !availableSeriesIndexes.has(index))
  }

  const titleDisplayItems = getReportTitleDisplayItems(reports.titles ?? [])

  const DataChips: React.FC<{
    unavailableTitles: Array<{ title: string, index: number }>
  }> = ({ unavailableTitles }) => {
    if (!unavailableTitles.length) {
      return null
    }

    return (
      <Box display={'flex'} flexWrap={'wrap'} gap={2} width="100%">
        {unavailableTitles.map(({ title, index }) => {
          const displayItem = titleDisplayItems[index]
          const fullTitle = displayItem?.fullTitle ?? title
          const displayTitle = displayItem?.displayTitle ?? title

          return (
            <ReportsChip
              key={index}
              index={-1}
              label={displayTitle}
              icon={<WarningAmberIcon sx={{ fontSize: '18px !important', color: `${grey[250]} !important` }} />}
              tooltipTitle={
                <Box>
                  <Typography variant="body2" fontWeight={600}>{fullTitle}</Typography>
                  <Typography variant="body2">
                    This comparison chart is not displayed because the minimum responses threshold was not met or no data was available for this survey item.
                  </Typography>
                </Box>
              }
              ariaLabel={fullTitle}
              maxWidth={320}
            />
          )
        })}
      </Box>
    )
  }

  const StatValues: React.FC<{
    showStats: ICalculation[]
    formatStatValue: (name: string, value: number | string) => string | number
  }> = ({ showStats, formatStatValue }) => (
    <Box display="flex" flexDirection="column" gap={2} alignItems="flex-end">
      {showStats?.map(({ name, values }) => (
        <Box
          key={name}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography fontWeight={600} color={black[100]} whiteSpace={'nowrap'}>{name}:</Typography>
          {comparisonStatsByName.has(name)
            ? (
              <React.Fragment>
                <ReportsChip
                  index={0}
                  label={formatStatValue(name, comparisonStatsByName.get(name)?.values?.[0] as number | string)}
                  backgroundColor={responseFilterBaselineColor}
                  textColor={responseFilterBaselineTextColor}
                />
                <ReportsChip
                  index={1}
                  label={formatStatValue(name, values?.[0] as number | string)}
                  backgroundColor={responseFilterActiveColor}
                />
              </React.Fragment>
              )
            : values?.map((value, index) => (
              <ReportsChip
                key={index}
                index={index}
                label={formatStatValue(name, value)}
              />
            ))}
        </Box>
      ))}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, mb: 2 }} >
      <Box display={'flex'} flexDirection={'column'} gap={2.5}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'flex-start'} gap={3}>
          <Box display="flex" flexDirection="column" gap={1} flex={1} minWidth={0}>
            <Typography variant="h6" color={black[100]} fontWeight="500">
              {titleText}
            </Typography>
            <QuestionSampleSizeRow stats={questionSampleStats} />
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2} flexShrink={0}>
            {hasRightSideControls && (
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" justifyContent="flex-end">
                {responseFilterWarningMessage && (
                  <CustomTooltip title={responseFilterWarningMessage}>
                    <Box display="flex" alignItems="center" aria-label="Too few responses warning icon">
                      <WarningAmberIcon sx={{ color: yellow[450] }} />
                    </Box>
                  </CustomTooltip>
                )}
                {showOrientationToggle && (
                  <Box display="inline-flex" role="group" aria-label="Survey response chart orientation">
                    <CustomTooltip title={verticalOrientationTooltip}>
                      <Box>
                        <GenericButton
                          buttonType={ButtonType.Secondary}
                          buttonSize={ButtonSize.Small}
                          icon={<AlignVerticalBottomIcon />}
                          aria-label="Display vertical bar chart"
                          aria-pressed={chartOrientation === 'vertical'}
                          onClick={() => {
                            if (chartOrientation !== 'vertical') {
                              onChartOrientationChange?.('vertical')
                            }
                          }}
                          sx={chartOrientationButtonSx(chartOrientation === 'vertical', true)}
                        />
                      </Box>
                    </CustomTooltip>
                    <CustomTooltip title={horizontalOrientationTooltip}>
                      <Box>
                        <GenericButton
                          buttonType={ButtonType.Secondary}
                          buttonSize={ButtonSize.Small}
                          icon={<AlignHorizontalLeftIcon />}
                          aria-label="Display horizontal bar chart"
                          aria-pressed={chartOrientation === 'horizontal'}
                          onClick={() => {
                            if (chartOrientation !== 'horizontal') {
                              onChartOrientationChange?.('horizontal')
                            }
                          }}
                          sx={chartOrientationButtonSx(chartOrientation === 'horizontal', false)}
                        />
                      </Box>
                    </CustomTooltip>
                  </Box>
                )}
                {showChartTypeDropdown && (
                  <Dropdown
                    label={reportChartTypeOptions.find(({ value }) => value === selectedChartType)?.label ?? 'Distribution'}
                    buttonType={ButtonType.Secondary}
                    buttonSize={ButtonSize.Small}
                    menuItems={reportChartTypeOptions.map(({ label, value }) => ({
                      text: label,
                      action: () => { onChartTypeChange?.(value) }
                    }))}
                    extraButtonProps={{
                      sx: {
                        px: 2.5,
                        minWidth: 140
                      }
                    }}
                    menuPaperStyles={{ minWidth: 180 }}
                  />
                )}
                {showStatsButtons && isLinearScaleQuestion && (
                  <StatButtonGroup
                    buttons={statButtons}
                    selected={selectedStat ?? KeyStatType.AVERAGE}
                    onSelect={(value) => { setSelectedStat?.(value as KeyStatType) }}
                  />
                )}
              </Box>
            )}
            {hasShowStats && !showStatsButtons && (
              <StatValues showStats={showStats} formatStatValue={formatStatValue} />
            )}
          </Box>
        </Box>
        <DataChips unavailableTitles={unavailableTitles} />
      </Box>
    </Box>
  )
}

export default Title
